import Box from '@mui/material/Box';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import { CriteriaPaneProps, RandomFailureProbability } from '../types';
import { CriteriaSection, EmptyHint, EntryCard, FieldLabel } from '../FormComponents';
import { PercentageField } from '../../index';
import ZuulQueryBuilder from '../../../QueryBuilder/ZuulQueryBuilder';
import ZuulLoader from '../../../ZuulLoader/ZuulLoader';
import { randomFailureFields } from '../../../../utils/queryBuilderFields';
import { useDomains } from '../../../../views/OfferBlocks/queries';
import { useSites } from '../../../../views/Sites/queries';
import { useAffiliates } from '../../../../views/Affiliates/queries';
import { useLandingPagesDtos } from '../../../../views/LandingPages/queries';
import { useLandingPageGroupsDtos } from '../../../../views/LandingPageGroups/queries';
import type { Entity } from '../../../../views/CustomProperties/EntityGroups/details/types';
import { HELP } from '../criteriaHelp';
import { hasValue } from './shared';
import type { CriteriaSectionDef } from './types';

/** Everything the condition builder can filter on, loaded once for the section. */
function useConditionFields() {
  const toEntities = (rows: { id: number; name: string }[]): Entity[] => rows.map(({ id, name }) => ({ id, name }));
  const domains = useDomains(true, toEntities);
  const sites = useSites((rows: { id: number; domain: string }[]): Entity[] =>
    rows.map((site) => ({ id: site.id, name: site.domain }))
  );
  const affiliates = useAffiliates(true, toEntities);
  const landingPages = useLandingPagesDtos((rows: { id: number; offer: { name: string } }[]): Entity[] =>
    rows.map((page) => ({ id: page.id, name: page.offer.name }))
  );
  const landingPageGroups = useLandingPageGroupsDtos(toEntities);

  const queries = [domains, sites, affiliates, landingPages, landingPageGroups];
  if (queries.some((q) => q.isLoading)) return null;
  return randomFailureFields(
    domains.data || [],
    affiliates.data || [],
    sites.data || [],
    landingPages.data || [],
    landingPageGroups.data || []
  );
}

function RandomFailureSection({ state, setState }: CriteriaPaneProps) {
  const fields = useConditionFields();
  const rows = state.randomFailureProbabilities || [];

  const setRows = (update: (list: RandomFailureProbability[]) => RandomFailureProbability[]) =>
    setState((s) => ({ ...s, randomFailureProbabilities: update(s.randomFailureProbabilities || []) }));
  const updateRow = (index: number, patch: Partial<RandomFailureProbability>) =>
    setRows((list) => list.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <CriteriaSection
      title='Random Failure Probabilities'
      help={HELP.randomFailure}
      actions={
        <Button color='primary' startIcon={<Add />} onClick={() => setRows((list) => [...list, { probability: '0' }])}>
          Add Failure Probability
        </Button>
      }
    >
      {rows.length === 0 && <EmptyHint>No random failures configured.</EmptyHint>}
      {rows.length > 0 && !fields && <ZuulLoader text='Loading Query Builder Fields...' />}
      {fields &&
        rows.map((row, index) => (
          <EntryCard
            key={index}
            title={`Failure ${index + 1}`}
            onRemove={() => setRows((list) => list.filter((_, i) => i !== index))}
            removeLabel='Remove this failure probability'
          >
            <FieldLabel help={HELP.randomFailureProbability}>Failure Probability</FieldLabel>
            <PercentageField
              input={{
                value: row.probability,
                onChange: (value: number) => updateRow(index, { probability: value.toString() }),
                onBlur: () => {},
              }}
              meta={{ error: null, touched: false }}
              label=''
            />
            <Box sx={{ mt: 1.5 }}>
              <FieldLabel help={HELP.randomFailureCondition}>Condition</FieldLabel>
            </Box>
            <ZuulQueryBuilder
              query={row.condition || ''}
              fields={fields}
              setQuery={(value: string) => updateRow(index, { condition: value })}
            />
          </EntryCard>
        ))}
    </CriteriaSection>
  );
}

export const ANALYSIS_SECTIONS: CriteriaSectionDef[] = [
  {
    id: 'randomFailure',
    tab: 'analysis',
    title: 'Random Failure Probabilities',
    fields: [],
    aliases: ['Failure Probability', 'Condition', 'random', 'failure', 'probability', 'analysis'],
    stateKeys: ['randomFailureProbabilities'],
    isConfigured: (s) => hasValue(s.randomFailureProbabilities),
    Component: RandomFailureSection,
  },
];
